import { useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Eye } from "lucide-react";
import { Section } from "@/components/ui/Section";
import { Button } from "@/components/ui/Button";
import { Reveal } from "@/components/common/Reveal";
import { useScrollTo } from "@/hooks/useScrollTo";
import { useModalEffects } from "@/hooks/useModalEffects";
import { PROJECTS } from "@/utils/constants";

export const FeaturedProjects = () => {
  const [active, setActive] = useState(null);
  const [shot, setShot] = useState(0);
  const scrollTo = useScrollTo();
  const modalRef = useRef(null);

  const close = useCallback(() => setActive(null), []);
  useModalEffects(!!active, close, modalRef);

  const open = (p) => { setShot(0); setActive(p); };
  const requestQuote = () => { setActive(null); setTimeout(() => scrollTo("contact"), 160); };

  return (
    <Section id="projects" testId="projects-section" bg="ink">
      <Reveal>
        <p className="text-weld-glow font-semibold tracking-[0.25em] uppercase text-sm mb-3">Portfolio</p>
        <h2 className="font-display text-5xl md:text-6xl text-white">Real Projects. Real Results.</h2>
        <p className="text-white/60 mt-4 max-w-2xl">Real welding, fabrication, repair, and custom metal projects completed by Deuce's Welding.</p>
      </Reveal>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
        {PROJECTS.map((p, i) => (
          <Reveal key={p.id} delay={i * 0.08}>
            <div data-testid={`project-card-${i}`} className="group flex flex-col overflow-hidden rounded-2xl border border-white/10 hover:border-weld/50 transition-colors bg-charcoal h-full">
              <button onClick={() => open(p)} className="relative aspect-[4/3] overflow-hidden">
                <img src={p.image} alt={p.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <span className="absolute top-4 left-4 text-weld-glow text-xs font-semibold uppercase tracking-[0.2em] bg-ink/70 border border-weld/30 rounded-full px-3 py-1">{p.category}</span>
              </button>
              <div className="p-6 flex flex-col flex-1">
                <h3 className="font-display text-3xl text-white">{p.title}</h3>
                <p className="text-white/60 text-sm mt-2 flex-1">{p.short}</p>
                <div className="flex flex-wrap gap-1.5 mt-4">
                  {p.services.map((s) => (
                    <span key={s} className="text-[10px] font-semibold text-white/60 border border-white/15 rounded-full px-2.5 py-1 uppercase tracking-wide">{s}</span>
                  ))}
                </div>
                <div className="flex gap-3 mt-5">
                  <Button data-testid={`project-view-${i}`} size="sm" onClick={() => open(p)}>
                    <Eye size={15} /> View Project
                  </Button>
                  <Button data-testid={`project-quote-${i}`} variant="outline" size="sm" onClick={() => scrollTo("contact")}>
                    Request Quote
                  </Button>
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="project-modal"
            className="fixed inset-0 z-[70] bg-ink/95 backdrop-blur-md flex items-start sm:items-center justify-center p-4 sm:p-6"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={close}
            data-lenis-prevent
          >
            <button data-testid="project-modal-close" aria-label="Close project" onClick={close} className="fixed top-5 right-5 z-[80] w-11 h-11 rounded-full bg-ink/80 border border-white/15 flex items-center justify-center text-white/80 hover:text-weld-glow hover:border-weld/50 transition-colors">
              <X size={22} />
            </button>
            <motion.div
              ref={modalRef}
              role="dialog"
              aria-modal="true"
              aria-label={active.title}
              tabIndex={-1}
              className="relative w-full max-w-3xl bg-charcoal border border-weld/20 rounded-2xl overflow-y-auto max-h-[90vh] my-auto outline-none"
              initial={{ scale: 0.94, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.94, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              data-lenis-prevent
            >
              <img src={active.gallery[shot]} alt={active.title} className="w-full aspect-[16/10] object-cover rounded-t-2xl" />
              {active.gallery.length > 1 && (
                <div className="flex gap-2 p-4 pb-0">
                  {active.gallery.map((g, gi) => (
                    <button key={gi} data-testid={`project-thumb-${gi}`} onClick={() => setShot(gi)} className={`w-16 h-16 rounded-lg overflow-hidden border-2 ${shot === gi ? "border-weld" : "border-white/10"}`}>
                      <img src={g} alt={`${active.title} view ${gi + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}

              <div className="p-6 sm:p-8">
                <p className="text-weld-glow text-xs font-semibold uppercase tracking-[0.2em] mb-2">{active.category}</p>
                <h3 className="font-display text-4xl text-white">{active.title}</h3>
                <p className="text-white/65 mt-4 leading-relaxed">{active.description}</p>

                <div className="grid sm:grid-cols-3 gap-4 mt-6">
                  <div className="bg-ink/60 rounded-lg p-4 border border-white/5">
                    <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Repair Type</p>
                    <p className="text-white text-sm font-semibold">{active.repairType}</p>
                  </div>
                  <div className="bg-ink/60 rounded-lg p-4 border border-white/5">
                    <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Materials</p>
                    <p className="text-white text-sm font-semibold">{active.materials}</p>
                  </div>
                  <div className="bg-ink/60 rounded-lg p-4 border border-white/5">
                    <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Process</p>
                    <p className="text-white text-sm font-semibold">{active.process}</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mt-6">
                  {active.services.map((s) => (
                    <span key={s} className="text-xs font-semibold text-weld-glow border border-weld/30 bg-weld/10 rounded-full px-3 py-1 uppercase tracking-wide">{s}</span>
                  ))}
                </div>

                <Button data-testid="project-modal-quote" size="lg" className="mt-7 w-full sm:w-auto" onClick={requestQuote}>
                  Request Quote <ArrowRight size={18} />
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Section>
  );
};
